import React from 'react';
import {
  Avatar,
  Typography,
  Box,
  Paper,
  IconButton,
  LinearProgress,
  Chip,
} from '@mui/material';
import Button from '@mui/material/Button';
import { logout, getCurrentUser } from '../../util/auth';
import { User, useUpdateUserImage } from '../../hooks/api/user';
import { useNavigate } from 'react-router-dom';
import { formatDateToMonthAndYear } from '../../util/dates';
import { useGetPlans } from '../../hooks/api/plan';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import LogoutIcon from '@mui/icons-material/Logout';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import AccountSwitcher from '../AccountSwitcher';

interface ProfileViewProps {
  user: User;
  canLogout?: boolean;
}

const beltColors: Record<string, string> = {
  white: '#f5f5f5',
  blue: '#1976d2',
  purple: '#7b1fa2',
  brown: '#5d4037',
  black: '#212121',
};

const beltNames: Record<string, string> = {
  white: 'Blanco',
  blue: 'Azul',
  purple: 'Morado',
  brown: 'Marrón',
  black: 'Negro',
};

export default function ProfileView({ user, canLogout }: ProfileViewProps) {
  const getPlansQuery = useGetPlans();
  const plans = getPlansQuery.data;
  const userPlan = plans?.find((plan) => plan.id === user?.plan?.id);
  const beltImage = user?.belt ?? 'white';

  const currentUser = getCurrentUser();
  const updateUserImageMutation = useUpdateUserImage();

  const [imageRefreshKey, setImageRefreshKey] = React.useState(0);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      updateUserImageMutation.mutate(
        {
          id: user.id,
          image: event.target.files[0],
        },
        {
          onSuccess: () => {
            setImageRefreshKey((k) => k + 1);
          },
        },
      );
    }
  };

  const image = user?.belt ? require(`../../assets/images/${beltImage}_Belt.png`) : undefined;
  const navigate = useNavigate();

  const handleLogout = () => {
    navigate('/login');
    logout();
  };

  const progressPercentage = userPlan?.lessons
    ? Math.min((user.planBookings / userPlan.lessons) * 100, 100)
    : 0;

  return (
    <Box sx={{ pb: 'calc(96px + env(safe-area-inset-bottom))' }}>
      {/* Profile Header Card */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #1a1a1a 0%, #333333 100%)',
          color: 'white',
          textAlign: 'center',
        }}
      >
        {/* Avatar with edit button */}
        <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
          <Avatar
            alt={user.firstName}
            src={user.image ? `${user.image}?refresh=${imageRefreshKey}` : undefined}
            sx={{
              width: 100,
              height: 100,
              fontSize: 36,
              border: '4px solid rgba(255,255,255,0.3)',
            }}
          />
          {currentUser?.sub === user.id.toString() && (
            <label htmlFor="profile-image-upload">
              <input
                id="profile-image-upload"
                hidden
                accept="image/*"
                type="file"
                onChange={handleImageChange}
                tabIndex={0}
              />
              <IconButton
                component="span"
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'grey.100' },
                  width: 32,
                  height: 32,
                }}
                aria-label="Cambiar foto"
              >
                <CameraAltIcon fontSize="small" />
              </IconButton>
            </label>
          )}
        </Box>

        {/* Name */}
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {`${user.firstName} ${user.lastName}`}
        </Typography>

        {/* Belt chip */}
        <Chip
          label={beltNames[user.belt] || user.belt}
          size="small"
          sx={{
            bgcolor: beltColors[user.belt] || '#grey',
            color: user.belt === 'white' ? 'text.primary' : 'white',
            fontWeight: 500,
          }}
        />
      </Paper>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        {/* Training since */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: 2,
            borderRadius: 2,
            bgcolor: 'grey.50',
            textAlign: 'center',
          }}
        >
          <CalendarTodayIcon sx={{ color: 'primary.main', mb: 0.5 }} />
          <Typography variant="caption" display="block" color="text.secondary">
            Entrenando desde
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatDateToMonthAndYear(new Date(user.startDate))}
          </Typography>
        </Paper>

        {/* Total bookings */}
        {user.totalBookings !== undefined && (
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: 2,
              borderRadius: 2,
              bgcolor: 'grey.50',
              textAlign: 'center',
            }}
          >
            <FitnessCenterIcon sx={{ color: 'primary.main', mb: 0.5 }} />
            <Typography variant="caption" display="block" color="text.secondary">
              Asistencias totales
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {user.totalBookings}
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Progress Card */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          bgcolor: 'grey.50',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body2" fontWeight={500}>
            Asistencias del plan
          </Typography>
          <Typography variant="body2" color="primary" fontWeight={600}>
            {user.planBookings} / {userPlan?.lessons ?? 0}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercentage}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
            },
          }}
        />
      </Paper>

      {/* Belt Display (read-only) */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          bgcolor: 'grey.50',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <img src={image} style={{ width: 56, height: 56 }} alt="belt" />
        <Box flex={1}>
          <Typography variant="caption" color="text.secondary" display="block">
            Cinturón actual
          </Typography>
          <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2, mb: 0.75 }}>
            {beltNames[user.belt] || user.belt}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Black belt-tip patch with white tape stripes */}
            <Box
              sx={{
                display: 'flex',
                gap: 0.625,
                px: 0.875,
                py: 0.625,
                borderRadius: 0.75,
                bgcolor: '#111',
                boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
              }}
            >
              {Array.from({ length: 4 }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 5,
                    height: 20,
                    borderRadius: 0.5,
                    bgcolor:
                      i < (user.stripes ?? 0) ? '#fff' : 'rgba(255,255,255,0.12)',
                  }}
                />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {user.stripes ?? 0} {(user.stripes ?? 0) === 1 ? 'grado' : 'grados'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Account Switcher & Logout */}
      {canLogout && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <AccountSwitcher />
          <Button
            variant="outlined"
            color="error"
            fullWidth
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Cerrar sesión
          </Button>
        </Box>
      )}
    </Box>
  );
}
